/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/BackgroundData/BackgroundData.tsx.
 */

// src/pages/BackgroundData.tsx
import React from "react";
import Button from "../../../../../components/common/Button";
import "../../../../../styles/formBase.css";
import "./BackgroundData.css";
import { FormField } from "../../../../../interfaces/formInterfaces";
import {
  getBackgroundDataReviewerTemplateFields,
  getBackgroundStageDisplayFields,
  partitionBackgroundFieldsAroundRawMaterials,
} from "../../../../../components/submission/backgroundStageFieldOrder";
import { useBackgroundData } from "./hooks/useBackgroundData";
import BackgroundFieldRenderer from "./components/BackgroundFieldRenderer";
import BackgroundFieldLabel from "./components/BackgroundFieldLabel";
import BackgroundRawMaterials from "./components/BackgroundRawMaterials";
import BackgroundDataReviewersBlock from "./components/BackgroundDataReviewersBlock";
import BackgroundGeographicalSpecificityField from "./components/BackgroundGeographicalSpecificityField";
import { isDataReviewerTemplateFieldName } from "./utils/backgroundDataReviewers";
import {
  BACKGROUND_GEO_SELECTION_FIELD,
  BACKGROUND_GEO_SPEC_FIELD,
} from "../../../../../utils/backgroundGeographicalSpecificity";

interface BackgroundDataProps {
  submissionId?: string;
  onNextStage: () => void | Promise<void>;
}

function backgroundFieldErrorKey(field: FormField): string {
  if (field.type === "MATERIALS") return "materialId";
  return field.name;
}

function BackgroundFormFieldCell({
  fieldName,
  errors,
  className,
  children,
}: {
  fieldName?: string;
  errors: Record<string, string>;
  className?: string;
  children: React.ReactNode;
}) {
  const error = fieldName ? errors[fieldName] : undefined;
  return (
    <div
      className={[className, error ? "field-has-error" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
      {error ? <span className="error-message">{error}</span> : null}
    </div>
  );
}

const BackgroundData: React.FC<BackgroundDataProps> = ({
  submissionId,
  onNextStage,
}) => {
  const {
    formData,
    values,
    setValues,
    loading,
    materials,
    subMaterials,
    rawMaterials,
    technologyOptions,
    manufacturerOptions,
    allUnits,
    enabledPairs,
    setEnabledPairs,
    rawMaterialGroups,
    reviewerGroups,
    externalDataReviewSelected,
    pairedGroups,
    addRawMaterialGroup,
    removeRawMaterialGroup,
    handleMaterialSelect,
    handleChange,
    handleFileChange,
    handleCheckboxChange,
    handleGeoSpecChange,
    handleGeoSelectionChange,
    stateOptions,
    geographicScopeOptions,
    geographicScopeLoading,
    handleSubmit,
    errors,
  } = useBackgroundData(submissionId);

  const handleSave = async () => {
    await handleSubmit();
  };

  const handleNext = async () => {
    const success = await handleSubmit();
    if (success) {
      await onNextStage();
    }
  };

  if (loading) return <p className="loading-text">Loading form...</p>;
  if (!formData) return <p>No form data available.</p>;

  const orderedAll = getBackgroundStageDisplayFields(formData);
  const { before: fieldsBeforeRaw, after: fieldsAfterRaw } =
    partitionBackgroundFieldsAroundRawMaterials(orderedAll);

  const { nameField: dataReviewerNameField, emailField: dataReviewerEmailField } =
    getBackgroundDataReviewerTemplateFields(formData);

  const fieldSequence: Array<
    { kind: "field"; field: FormField } | { kind: "raw" }
  > = [
    ...fieldsBeforeRaw.map((field) => ({ kind: "field" as const, field })),
    { kind: "raw" as const },
    ...fieldsAfterRaw.map((field) => ({ kind: "field" as const, field })),
  ];

  const subMaterialUnitField = orderedAll.find((f) => f.name === "subMaterialUnit");
  const rawMaterialUnitTemplateField =
    orderedAll.find((f) => f.name === "rawMaterialUnit_01") ??
    orderedAll.find((f) => /^rawMaterialUnit_/.test(f.name));
  const backgroundRawMaterialUnitPropertyId =
    rawMaterialUnitTemplateField?.unitPropertyId ??
    subMaterialUnitField?.unitPropertyId;

  const materialPropsYes = (() => {
    const s = String(values.materialPropertiesAvailable ?? "").trim().toLowerCase();
    return s === "yes" || s === "y" || s === "true" || s === "1";
  })();

  // Compute data completeness display based on A1/A2/A3/A1-A3 checkboxes
  const getDataCompletenessDisplay = (): string => {
    const a1 = (values["a1"] as string) === "true";
    const a2 = (values["a2"] as string) === "true";
    const a3 = (values["a3"] as string) === "true";
    const a1_a3 = (values["a1_a3"] as string) === "true";

    if (a1 && a2 && a3) return "A1, A2, AND A3 life cycle stage data reported";
    if (a1_a3 && !a1 && !a2 && !a3) return "Aggregated A1-A3 life cycle data reported";
    const count = [a1, a2, a3].filter(Boolean).length;
    if (count === 2) return "Any two life cycle stage data reported";
    if (count === 1) return "Any one life cycle stage data reported";
    return "";
  };

  // Field groups that should be skipped in individual rendering
  const subMaterialGroupFields = [
    "subMaterialId",
    "finishedQuantity",
    "subMaterialUnit",
  ];

  const startTimelineFields = [
    "collectionStartYear",
    "collectionStartMonth",
    "collectionStartDay",
  ];
  const endTimelineFields = [
    "collectionEndYear",
    "collectionEndMonth",
    "collectionEndDay",
  ];

  /** Paired numeric fields hidden from UI (values may still exist from prefill / defaults). */
  const hiddenPairedNumericFields = ["temperature"];

  return (
    <div className="form-container background-form-container contributor-stage-form">
      <h2 className="form-heading">{formData.name}</h2>
      {formData.description && (
        <p className="form-subtext">{formData.description}</p>
      )}

      <form
        className="form-content form-content-background"
        onSubmit={(e) => e.preventDefault()}
      >
        {fieldSequence.map((item) => {
          if (item.kind === "raw") {
            return (
              <BackgroundRawMaterials
                key="raw-materials-section"
                rawMaterialGroups={rawMaterialGroups}
                values={values}
                rawMaterials={rawMaterials}
                allUnits={allUnits}
                unitPropertyId={backgroundRawMaterialUnitPropertyId}
                onAddGroup={addRawMaterialGroup}
                onRemoveGroup={removeRawMaterialGroup}
                onChange={handleChange}
                errors={errors}
              />
            );
          }

          const field = item.field;

          if (!field.isEnabled) return null;

          // Skip duplicate rendering for Sub Material group
          if (
            subMaterialGroupFields.includes(field.name) &&
            field.name !== "subMaterialId"
          ) {
            return null;
          }

          // Skip duplicate rendering for Unit-Pair group fields
          if (Object.values(pairedGroups).includes(field.name)) {
            return null;
          }

          // Skip duplicate fields inside Timeline groups
          if (
            [...startTimelineFields, ...endTimelineFields].includes(
              field.name
            ) &&
            field.name !== "collectionStartYear" &&
            field.name !== "collectionEndYear"
          ) {
            return null;
          }

          if (hiddenPairedNumericFields.includes(field.name)) {
            return null;
          }

          // GROUP: Sub Material + Finished Qty + Sub Material Unit
          if (field.name === "subMaterialId") {
            const f2 = orderedAll.find((f) => f.name === "finishedQuantity");
            const f3 = orderedAll.find((f) => f.name === "subMaterialUnit");

            return (
              <div key="subMaterial-group" className="three-field-row">
                {[field, f2, f3].map(
                  (f) =>
                    f && (
                      <BackgroundFormFieldCell
                        key={f.id}
                        fieldName={backgroundFieldErrorKey(f)}
                        errors={errors}
                        className="three-field-cell"
                      >
                        <BackgroundFieldLabel
                          label={f.label}
                          isMandatory={f.isMandatory}
                          infoText={f.infoText}
                        />
                        <BackgroundFieldRenderer
                          field={f}
                          values={values}
                          materials={materials}
                          subMaterials={subMaterials}
                          rawMaterials={rawMaterials}
                          allUnits={allUnits}
                          enabledPairs={enabledPairs}
                          pairedGroups={pairedGroups}
                          technologyOptions={technologyOptions}
                          manufacturerOptions={manufacturerOptions}
                          onMaterialSelect={handleMaterialSelect}
                          onChange={handleChange}
                          onFileChange={handleFileChange}
                          onCheckboxChange={handleCheckboxChange}
                        />
                      </BackgroundFormFieldCell>
                    )
                )}
              </div>
            );
          }

          // GROUP: Paired Numeric + Unit fields (Density, U-Value, etc.)
          if (pairedGroups[field.name]) {
            const pairedField = orderedAll.find(
              (f) => f.name === pairedGroups[field.name]
            );

            if (pairedField) {
              const mainKey = field.name;
              const disablePropertyEnable = !materialPropsYes;

              return (
                <div key={`${field.name}-pair`} className="three-field-row">
                  {/* Enable Switch */}
                  <div className="three-field-cell">
                    <BackgroundFieldLabel
                      label={`Enable ${field.label}`}
                      isMandatory={field.isMandatory}
                      infoText={field.infoText}
                    />
                    <select
                      value={enabledPairs[mainKey] ? "yes" : "no"}
                      disabled={disablePropertyEnable}
                      onChange={(e) => {
                        if (disablePropertyEnable) return;
                        const enable = e.target.value === "yes";
                        setEnabledPairs((prev) => ({
                          ...prev,
                          [mainKey]: enable,
                        }));

                        if (!enable) {
                          setValues((prev) => ({
                            ...prev,
                            [mainKey]: "",
                            [pairedGroups[mainKey]]: "",
                          }));
                        }
                      }}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>

                  {/* Value + Unit renders */}
                  {[field, pairedField].map((f) => (
                    <BackgroundFormFieldCell
                      key={f.id}
                      fieldName={backgroundFieldErrorKey(f)}
                      errors={errors}
                      className="three-field-cell"
                    >
                      <BackgroundFieldLabel
                        label={f.label}
                        isMandatory={f.isMandatory}
                        infoText={f.infoText}
                      />
                      <BackgroundFieldRenderer
                        field={f}
                        values={values}
                        materials={materials}
                        subMaterials={subMaterials}
                        rawMaterials={rawMaterials}
                        allUnits={allUnits}
                        enabledPairs={enabledPairs}
                        pairedGroups={pairedGroups}
                        technologyOptions={technologyOptions}
                        manufacturerOptions={manufacturerOptions}
                        disabled={disablePropertyEnable}
                        onMaterialSelect={handleMaterialSelect}
                        onChange={handleChange}
                        onFileChange={handleFileChange}
                        onCheckboxChange={handleCheckboxChange}
                      />
                    </BackgroundFormFieldCell>
                  ))}
                </div>
              );
            }
          }

          // GROUP: Timeline Start
          if (field.name === "collectionStartYear") {
            const f2 = orderedAll.find((f) => f.name === "collectionStartMonth");
            const f3 = orderedAll.find((f) => f.name === "collectionStartDay");

            const isTimeline = values.timelineAvailable === "yes";

            return (
              <div key="timeline-start-group" className="three-field-row">
                {[field, f2, f3].map(
                  (f) =>
                    f && (
                      <BackgroundFormFieldCell
                        key={f.id}
                        fieldName={backgroundFieldErrorKey(f)}
                        errors={errors}
                        className="three-field-cell"
                      >
                        <BackgroundFieldLabel
                          label={f.label}
                          isMandatory={f.isMandatory}
                          infoText={f.infoText}
                        />
                        <BackgroundFieldRenderer
                          field={{
                          ...f,
                          isEditable: isTimeline,
                          isEnabled: isTimeline,
                          }}
                          values={values}
                          materials={materials}
                          subMaterials={subMaterials}
                          rawMaterials={rawMaterials}
                          allUnits={allUnits}
                          enabledPairs={enabledPairs}
                          pairedGroups={pairedGroups}
                          technologyOptions={technologyOptions}
                          manufacturerOptions={manufacturerOptions}
                          onMaterialSelect={handleMaterialSelect}
                          onChange={handleChange}
                          onFileChange={handleFileChange}
                          onCheckboxChange={handleCheckboxChange}
                        />
                      </BackgroundFormFieldCell>
                    )
                )}
              </div>
            );
          }

          // GROUP: Timeline End
          if (field.name === "collectionEndYear") {
            const f2 = orderedAll.find((f) => f.name === "collectionEndMonth");
            const f3 = orderedAll.find((f) => f.name === "collectionEndDay");

            const isTimeline = values.timelineAvailable === "yes";

            return (
              <div key="timeline-end-group" className="three-field-row">
                {[field, f2, f3].map(
                  (f) =>
                    f && (
                      <BackgroundFormFieldCell
                        key={f.id}
                        fieldName={backgroundFieldErrorKey(f)}
                        errors={errors}
                        className="three-field-cell"
                      >
                        <BackgroundFieldLabel
                          label={f.label}
                          isMandatory={f.isMandatory}
                          infoText={f.infoText}
                        />
                        <BackgroundFieldRenderer
                          field={{
                          ...f,
                          isEditable: isTimeline,
                          isEnabled: isTimeline,
                          }}
                          values={values}
                          materials={materials}
                          subMaterials={subMaterials}
                          rawMaterials={rawMaterials}
                          allUnits={allUnits}
                          enabledPairs={enabledPairs}
                          pairedGroups={pairedGroups}
                          technologyOptions={technologyOptions}
                          manufacturerOptions={manufacturerOptions}
                          onMaterialSelect={handleMaterialSelect}
                          onChange={handleChange}
                          onFileChange={handleFileChange}
                          onCheckboxChange={handleCheckboxChange}
                        />
                      </BackgroundFormFieldCell>
                    )
                )}
              </div>
            );
          }

          // GROUP: Data Completeness → A1/A2/A3/A1-A3 checkboxes → Timeline Available
          if (field.name === "a1" && field.type === "CHECKBOX") {
            const dataCompletenessField = orderedAll.find((f) => f.name === "dataCompleteness");
            const timelineField = orderedAll.find((f) => f.name === "timelineAvailable");
            const a2Field = orderedAll.find((f) => f.name === "a2");
            const a3Field = orderedAll.find((f) => f.name === "a3");
            const a1_a3Field = orderedAll.find((f) => f.name === "a1_a3");

            return (
              <div
                key="data-completeness-checkbox-timeline-group"
                className="background-full-width-group"
              >
                <div className="two-field-row data-completeness-geo-row">
                  <div className="two-field-row-inner data-completeness-geo-row-inner">
                    <div className="two-field-cell data-completeness-stack">
                      {dataCompletenessField && (
                        <div key={dataCompletenessField.id}>
                          <BackgroundFieldLabel
                            label={dataCompletenessField.label}
                            isMandatory
                            infoText={dataCompletenessField.infoText}
                          />
                          <input
                            type="text"
                            value={getDataCompletenessDisplay()}
                            readOnly
                            className="readonly-input"
                            placeholder="A1-A3 is required; A1, A2, and A3 are optional"
                          />
                        </div>
                      )}
                      <div key="checkbox-group" className="checkbox-group-row">
                        {[field, a2Field, a3Field, a1_a3Field].map(
                          (f) =>
                            f && (
                              <div key={f.id} className="checkbox-group-item">
                                <BackgroundFieldRenderer
                                  field={f}
                                  showMandatory={f.name === "a1_a3"}
                                  values={values}
                                  materials={materials}
                                  subMaterials={subMaterials}
                                  rawMaterials={rawMaterials}
                                  allUnits={allUnits}
                                  enabledPairs={enabledPairs}
                                  pairedGroups={pairedGroups}
                                  technologyOptions={technologyOptions}
                                  manufacturerOptions={manufacturerOptions}
                                  onMaterialSelect={handleMaterialSelect}
                                  onChange={handleChange}
                                  onFileChange={handleFileChange}
                                  onCheckboxChange={handleCheckboxChange}
                                />
                              </div>
                            )
                        )}
                      </div>
                      {errors.dataCompleteness ? (
                        <span className="error-message checkbox-group-error">
                          {errors.dataCompleteness}
                        </span>
                      ) : null}
                    </div>
                    <BackgroundGeographicalSpecificityField
                      cellClassName="two-field-cell"
                      geoSpec={String(values[BACKGROUND_GEO_SPEC_FIELD] ?? "global")}
                      geoSelection={String(values[BACKGROUND_GEO_SELECTION_FIELD] ?? "")}
                      stateOptions={stateOptions}
                      geographicScopeOptions={geographicScopeOptions}
                      geographicScopeLoading={geographicScopeLoading}
                      onGeoSpecChange={handleGeoSpecChange}
                      onGeoSelectionChange={handleGeoSelectionChange}
                      error={errors[BACKGROUND_GEO_SELECTION_FIELD]}
                    />
                  </div>
                </div>
                {/* Timeline Available (full width) */}
                {timelineField && (
                  <BackgroundFormFieldCell
                    key={timelineField.id}
                    fieldName={backgroundFieldErrorKey(timelineField)}
                    errors={errors}
                    className="field-row data-completeness-row"
                  >
                    <BackgroundFieldLabel
                      label={timelineField.label}
                      isMandatory={timelineField.isMandatory}
                      infoText={timelineField.infoText}
                      ensureQuestionMark
                    />
                    <BackgroundFieldRenderer
                      field={timelineField}
                      values={values}
                      materials={materials}
                      subMaterials={subMaterials}
                      rawMaterials={rawMaterials}
                      allUnits={allUnits}
                      enabledPairs={enabledPairs}
                      pairedGroups={pairedGroups}
                      technologyOptions={technologyOptions}
                      manufacturerOptions={manufacturerOptions}
                      onMaterialSelect={handleMaterialSelect}
                      onChange={handleChange}
                      onFileChange={handleFileChange}
                      onCheckboxChange={handleCheckboxChange}
                    />
                  </BackgroundFormFieldCell>
                )}
              </div>
            );
          }

          // Rendered inside data-completeness-checkbox-timeline-group (at `a1`)
          if (["dataCompleteness", "timelineAvailable"].includes(field.name)) {
            return null;
          }

          // Skip other checkbox fields if they're part of the A1-A3 group
          if (
            ["a2", "a3", "a1_a3"].includes(field.name) &&
            field.type === "CHECKBOX"
          ) {
            return null;
          }

          if (isDataReviewerTemplateFieldName(field.name)) {
            return null;
          }

          if (field.name === "dataReview") {
            const dataAcquisitionField = orderedAll.find(
              (f) => f.name === "dataAcquisition"
            );

            const renderDataReviewFieldCell = (
              f: FormField,
              cellClassName = "two-field-cell"
            ) => (
              <BackgroundFormFieldCell
                key={f.id}
                fieldName={backgroundFieldErrorKey(f)}
                errors={errors}
                className={cellClassName}
              >
                <BackgroundFieldLabel
                  label={f.label}
                  isMandatory={f.isMandatory}
                  infoText={f.infoText}
                />
                <BackgroundFieldRenderer
                  field={f}
                  values={values}
                  materials={materials}
                  subMaterials={subMaterials}
                  rawMaterials={rawMaterials}
                  allUnits={allUnits}
                  enabledPairs={enabledPairs}
                  pairedGroups={pairedGroups}
                  technologyOptions={technologyOptions}
                  manufacturerOptions={manufacturerOptions}
                  onMaterialSelect={handleMaterialSelect}
                  onChange={handleChange}
                  onFileChange={handleFileChange}
                  onCheckboxChange={handleCheckboxChange}
                />
              </BackgroundFormFieldCell>
            );

            return (
              <div
                key={`${field.id}-data-review-group`}
                className="field-row data-review-acquisition-group"
              >
                <div className="two-field-row-inner data-review-acquisition-row">
                  {renderDataReviewFieldCell(field)}
                  {dataAcquisitionField
                    ? renderDataReviewFieldCell(dataAcquisitionField)
                    : null}
                </div>
                <BackgroundDataReviewersBlock
                  active={externalDataReviewSelected}
                  reviewerGroups={reviewerGroups}
                  values={values}
                  onChange={handleChange}
                  errors={errors}
                  nameField={dataReviewerNameField}
                  emailField={dataReviewerEmailField}
                />
              </div>
            );
          }

          if (field.name === "dataAcquisition") {
            return null;
          }

          // DEFAULT FIELD RENDER
          const isFullWidthField = field.name === "materialPropertiesAvailable";
          const errorKey = backgroundFieldErrorKey(field);
          return (
            <BackgroundFormFieldCell
              key={field.id}
              fieldName={errorKey}
              errors={errors}
              className={`field-row ${isFullWidthField ? "data-completeness-row" : ""}`}
            >
              <BackgroundFieldLabel
                label={field.label}
                isMandatory={field.isMandatory}
                infoText={field.infoText}
              />
              <BackgroundFieldRenderer
                field={field}
                values={values}
                materials={materials}
                subMaterials={subMaterials}
                rawMaterials={rawMaterials}
                allUnits={allUnits}
                enabledPairs={enabledPairs}
                pairedGroups={pairedGroups}
                technologyOptions={technologyOptions}
                manufacturerOptions={manufacturerOptions}
                onMaterialSelect={handleMaterialSelect}
                onChange={handleChange}
                onFileChange={handleFileChange}
                onCheckboxChange={handleCheckboxChange}
              />
            </BackgroundFormFieldCell>
          );
        })}

        {/* Buttons */}
        <div className="button-row">
          <Button label="Save" onClick={handleSave} />
          <Button label="Next" onClick={handleNext} />
        </div>
      </form>
    </div>
  );
};

/** React component — Background Data. */
export default BackgroundData;
